import React, { ChangeEvent, useState, useEffect } from "react";
import {
  LoadingButton,
  InputField,
  LoadingState,
  TextButton,
} from "kubra-ux-forge";
import triangleWarning from "assets/icons/triangle_exclamation.svg";
import { getBiller } from "selectors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { COMMON_NAMESPACE, SMS_VIEW } from "constants/i18n-namespaces";
import { PLATFORM_NAMESPACE } from "kubra-labels";
import XButton from "assets/icons/x_button.svg";
import { isPhone, postSmslWithInstanceId, postSmslWithSpaceKrn } from "utilities/email-sms-util";

export interface ISMSModalProps {
  accountNumber: any;
  barcodeText: any;
  billerName: any;
  cancelHandler: (arg0: string) => void;
  smsInputChange: (arg0: string) => void;
}

export const SMSModal = (props: ISMSModalProps) => {
  const { accountNumber, barcodeText, billerName, cancelHandler, smsInputChange } = props;
  const { t } = useTranslation(SMS_VIEW);
  const [showInvalidPhone, setShowInvalidPhone] = useState(false);
  const [showEmptyPhone, setShowEmptyPhone] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorState, setErrorState] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<LoadingState>("text");
  const [sentSms, setSentSms] = useState(false);
  const biller = useSelector(getBiller);

  const getFormattedPhoneNum = (input: string) => {
    let output = "(";
    // @ts-ignore
    input.replace(/^\D*(\d{0,3})\D*(\d{0,3})\D*(\d{0,4})/, function (match, g1, g2, g3) {
      if (g1.length) {
        output += g1;
        if (g1.length === 3) {
          output += ")";
          if (g2.length) {
            output += " " + g2;
            if (g2.length === 3) {
              output += " - ";
              if (g3.length) {
                output += g3;
              }
            }
          }
        }
      }
    }
    );
    return output;
  };

  const updatePhoneNumber = (e: string) => {
    setShowInvalidPhone(false);
    setShowEmptyPhone(false);
    setShowErrorMessage(false);
    setErrorState(false);
    setPhoneNumber(getFormattedPhoneNum(e));
  };
  

  const sendSms = async () => {
    if (!phoneNumber || phoneNumber.length === 0) {
      setShowEmptyPhone(true);
      setErrorState(true);
    }
    
    let apiSuccess;
    
    const numPhone = phoneNumber.replace(/\D/g, "");
    if (isPhone(numPhone)) {
      setSending(true);
      setSuccess("loading");

      apiSuccess = (!(biller?.featureFlags?.accountV2) || biller?.featureFlags?.accountV2?.toLowerCase() === 'false') ? 
       await postSmslWithInstanceId(biller, numPhone) : await postSmslWithSpaceKrn(biller, numPhone)

      setSentSms(true);
      if (apiSuccess) {
        setSuccess("success");
      }
      else {
        setSuccess("text");
        setShowErrorMessage(true);
      }
      setSending(false);

    }
    else {
      setShowInvalidPhone(true);
      setErrorState(true);
    }
    
  };

  useEffect(() => {
    const timer = setTimeout(() => {
        if (success === "success") {
            cancelHandler("SmsModalSuccessClose");
            smsInputChange(phoneNumber);
        }
    }, 1000);
  });

  let sentSuccess = false;
  if (success === "success") {
    sentSuccess = true;
  }

  return (
    <>
    <div className="modal-header">
        <div className="help-modal-header-text">
        {t("Send to mobile number")}
        </div>
        <button
          className="modal-close"
          onClick={() => cancelHandler("BarcodeView")}
          aria-label={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
        >
          <img
            src={XButton}
            alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
            className="modal-close-x"
          />
        </button>
    </div>
    <div data-cy="sms-modal" className="send-sms-frame-container">
      <div className="send-sms-frame">
      <div className="content-row">
        <div className="left-text-14">{t("sms-disclaimer")}</div>
      </div>
      <div className="content-row">
        <InputField
          defaultValue=""
          errorText=""
          helperText=""
          label={`${t("mobile-number-label")}`}
          onChange={(e:ChangeEvent<HTMLInputElement>) => updatePhoneNumber(e.target.value)}
          value={phoneNumber}
          hasError={errorState}
          title={`${t("mobile-number-title")}`}
          dataCy="input-mobile-number"
        />
        {showInvalidPhone && (
          <div>
            <img
              src={triangleWarning}
              alt="error"
              className="floatLeft"
              aria-hidden={true}
            />
            <div className="errorText">{t("mobile-number-invalid")}</div>
          </div>
        )}
        {showEmptyPhone && (
          <div>
            <img
              src={triangleWarning}
              alt="error"
              className="floatLeft"
              aria-hidden={true}
            />
            <div className="errorText">{t("mobile-number-required")}</div>
          </div>
        )}
        {showErrorMessage && (
          <div>
            <img
              src={triangleWarning}
              alt="error"
              className="floatLeft"
              aria-hidden={true}
            />
            <div className="errorText">{t("error-sending-sms")}</div>
          </div>
        )}
      </div>
      <div className="content-row">
        <div className="content-row-left-button">
          <TextButton
            onClick={() => props.cancelHandler("BarcodeView")}
            className="cancel-button"
            title="cancel button"
            dataCy="button-cancel"
          >
            {t("cancelLabel", { ns: PLATFORM_NAMESPACE })}
          </TextButton>
        </div>
        <div className="content-row-right-button">
          <LoadingButton
            className="send-button"
            onClick={() => {
              sendSms();
            }}
            loadingState={success}
            title="send button"
            dataCy="button-send"
          >
            {t("send", { ns: COMMON_NAMESPACE })}
          </LoadingButton>
        </div>
      </div>
      {sentSuccess && <div title="sent success"></div>}
    </div>
    </div>
    </>
  );
}
